import React from "react";
import { Phone, Mail, MapPin, Clock, ArrowRight } from "lucide-react";
import { SITE } from "../../site.config";
import QuoteButton from "./QuoteButton";

export default function Contact() {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-black py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Info side */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-cyan-pz" />
              <span className="bebas text-cyan-pz tracking-[0.3em] text-sm">
                GET IN TOUCH
              </span>
            </div>
            <h2 className="bebas text-white text-5xl md:text-7xl leading-[0.9] mb-6">
              READY WHEN <br />
              <span className="text-cyan-pz">YOU ARE.</span>
            </h2>
            <p className="text-white/70 barlow text-base md:text-lg leading-relaxed mb-10">
              The fastest way to get a quote or book in is to give us a bell.
              Phone is our preferred contact — we'll talk it through and have
              you sorted on the spot. Out of hours or sending photos? Shoot us
              an email and we'll get back to you next business day.
            </p>

            <div className="space-y-5">
              <a
                href={`tel:${SITE.business.phoneTel}`}
                data-testid="contact-phone-link"
                className="flex items-start gap-4 group"
              >
                <div className="w-11 h-11 border border-cyan-pz/40 flex items-center justify-center bg-cyan-pz/5 group-hover:bg-cyan-pz group-hover:text-black transition-all">
                  <Phone className="w-5 h-5 text-cyan-pz group-hover:text-black" />
                </div>
                <div>
                  <p className="bebas text-white/50 tracking-widest text-xs">CALL US</p>
                  <p className="bebas text-white text-2xl tracking-wider group-hover:text-cyan-pz transition-colors">
                    {SITE.business.phone}
                  </p>
                </div>
              </a>

              <a
                href={`mailto:${SITE.business.email}`}
                data-testid="contact-email-link"
                className="flex items-start gap-4 group"
              >
                <div className="w-11 h-11 border border-cyan-pz/40 flex items-center justify-center bg-cyan-pz/5 group-hover:bg-cyan-pz transition-all">
                  <Mail className="w-5 h-5 text-cyan-pz group-hover:text-black" />
                </div>
                <div className="min-w-0">
                  <p className="bebas text-white/50 tracking-widest text-xs">EMAIL</p>
                  <p className="text-white text-base barlow break-all group-hover:text-cyan-pz transition-colors">
                    {SITE.business.email}
                  </p>
                </div>
              </a>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 border border-cyan-pz/40 flex items-center justify-center bg-cyan-pz/5">
                  <MapPin className="w-5 h-5 text-cyan-pz" />
                </div>
                <div>
                  <p className="bebas text-white/50 tracking-widest text-xs">WORKSHOP</p>
                  <p className="text-white text-base barlow">
                    {SITE.business.address.full}
                  </p>
                  <p className="text-white/50 text-sm barlow">
                    {SITE.business.serviceArea}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 border border-cyan-pz/40 flex items-center justify-center bg-cyan-pz/5">
                  <Clock className="w-5 h-5 text-cyan-pz" />
                </div>
                <div>
                  <p className="bebas text-white/50 tracking-widest text-xs">HOURS</p>
                  {SITE.business.hours.map((h) => (
                    <p key={h.day} className="text-white text-sm barlow">
                      <span className="text-white/60">{h.day}: </span>
                      {h.time}
                    </p>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-10 border border-white/10 overflow-hidden">
              <iframe
                title="Powerz Auto Electrical workshop location"
                src={`https://www.google.com/maps?q=${encodeURIComponent(
                  SITE.business.address.full
                )}&output=embed`}
                width="100%"
                height="240"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                style={{ border: 0, filter: "invert(0.9) hue-rotate(180deg) saturate(0.6)" }}
                data-testid="contact-map"
              />
            </div>
          </div>

          {/* Right side — Call-first CTA panel */}
          <div className="lg:col-span-7">
            <div
              data-testid="contact-cta-panel"
              className="relative bg-[#0A0A0A] border border-white/10 p-8 md:p-12 clip-perf h-full flex flex-col"
            >
              <div className="inline-flex items-center gap-2 self-start border border-cyan-pz/40 bg-cyan-pz/5 px-3 py-1.5 mb-6">
                <span className="w-2 h-2 rounded-full bg-cyan-pz pulse-cyan" />
                <span className="bebas text-cyan-pz tracking-[0.25em] text-xs">
                  PREFERRED CONTACT — PHONE
                </span>
              </div>

              <h3 className="bebas text-white text-4xl md:text-6xl leading-[0.9] tracking-wider mb-6">
                GIVE US A <br />
                <span className="text-cyan-pz glow-cyan">BELL.</span>
              </h3>

              <p className="text-white/70 barlow text-base md:text-lg leading-relaxed mb-8 max-w-xl">
                We'd rather chat it out than swap a dozen emails. One quick call
                and we'll size up the job, give you a ballpark on the spot, and
                book you in. Workshop's at Kunda Park, servicing all the
                Sunshine Coast.
              </p>

              <a
                href={`tel:${SITE.business.phoneTel}`}
                data-testid="contact-cta-call-btn"
                className="inline-flex items-center justify-center gap-3 bg-cyan-pz text-black px-8 py-5 bebas text-2xl tracking-[0.2em] hover:bg-white transition-colors clip-perf pulse-cyan self-start"
              >
                <Phone className="w-6 h-6" />
                CALL {SITE.business.phone}
              </a>

              <div className="mt-6 inline-flex items-center gap-2 text-white/50 text-sm barlow">
                <Clock className="w-4 h-4 text-cyan-pz/60" />
                Phone lines open: Mon–Thu 7:30am–4pm • Fri 7:30am–2pm
              </div>

              <div className="my-10 flex items-center gap-4">
                <span className="h-px flex-1 bg-white/10" />
                <span className="bebas tracking-[0.3em] text-white/40 text-xs">
                  OR
                </span>
                <span className="h-px flex-1 bg-white/10" />
              </div>

              <p className="bebas tracking-widest text-white/50 text-xs mb-3">
                AFTER HOURS / NEED A WRITTEN QUOTE?
              </p>
              <QuoteButton variant="solid" testId="contact-cta-quote-btn" className="self-start mb-4" />
              <a
                href={`mailto:${SITE.business.email}?subject=Powerz%20Auto%20Electrical%20Enquiry`}
                data-testid="contact-cta-email-btn"
                className="group inline-flex items-center justify-between gap-4 border border-white/15 hover:border-cyan-pz bg-black px-6 py-5 transition-colors clip-perf"
              >
                <div className="flex items-center gap-4 min-w-0">
                  <Mail className="w-5 h-5 text-cyan-pz shrink-0" />
                  <span className="text-white barlow text-base md:text-lg break-all group-hover:text-cyan-pz transition-colors">
                    {SITE.business.email}
                  </span>
                </div>
                <ArrowRight className="w-5 h-5 text-white/40 group-hover:text-cyan-pz group-hover:translate-x-1 transition-all shrink-0" />
              </a>
              <p className="mt-3 text-white/40 text-xs barlow">
                Emails answered next business day. For urgent jobs please call.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
